import { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FormattedMessage, useIntl } from 'react-intl';
import { CashFlow, GroupedCashFlow, TransactionCategory } from '@algoan/dashboard/model';
import { useSelector } from '@algoan/dashboard/store/store';
import { Button, Typography, Divider, FormattedMoney } from '../atomic-components';
import { messages } from './cash-flow-modal-messages';
import { ModalTransactionSection } from './cash-flow-modal-transactions-list';
import { bankStatementsWithLoanAccountsSelector } from '../../modules/bank-statements/bank-statements-selectors';
import { accountMessages, useTransactionTypeTranslator, useTransactionCategoryTranslator } from '../../messages';
import { ModalDropdown } from './cash-flow-modal-dropdown';
import { categoriesListEntry, categoriesListOut } from './categories-list';
import { isAnalysisCashFlow, monitoring } from '@algoan/dashboard/utils';
import { addAlpha } from '@algoan/deep-dive/theme';

interface CashFlowModalProps {
  cashFlow: GroupedCashFlow | CashFlow | undefined;
  onClose(): void;
  isModalOpen: boolean;
  oldCategory?: TransactionCategory;
  newCategory?: TransactionCategory;
  isLoadingModal?: boolean;
  onChangeCategory: (selectedCategory: TransactionCategory) => Promise<void>;
  analysisId: string | undefined;
  customerId: string;
}

export const CashFlowModal: React.FC<CashFlowModalProps> = ({
  cashFlow,
  onClose,
  isModalOpen,
  oldCategory,
  newCategory,
  isLoadingModal,
  onChangeCategory,
  analysisId,
  customerId,
}) => {
  const intl = useIntl();
  const translateTransactionType = useTransactionTypeTranslator();
  const translateTransactionCategory = useTransactionCategoryTranslator();

  const bankStatements = useSelector(bankStatementsWithLoanAccountsSelector);
  const [selectedCategory, setSelectedCategory] = useState<TransactionCategory>(newCategory as TransactionCategory);

  useEffect(() => {
    cashFlow && setSelectedCategory(cashFlow.category);
  }, [cashFlow, isModalOpen]);

  if (isLoadingModal || !cashFlow) {
    return (
      <S.LoadingMessageContainer>
        <S.LoadingMessage>
          <FormattedMessage
            id="cash_flow_modal.loading_transactions"
            defaultMessage="Loading transactions..."
            description="Message to indicate the modal is loading transactions"
          />
        </S.LoadingMessage>
      </S.LoadingMessageContainer>
    );
  }

  const type = cashFlow.type;
  const totalCashFlow = cashFlow && (isAnalysisCashFlow(cashFlow) ? cashFlow.total : cashFlow.totalAmount);
  const cashFlowTransactionId =
    'transactions' in cashFlow ? cashFlow.transactions?.map((transaction) => transaction.id) : undefined;

  const transactionsList =
    cashFlowTransactionId &&
    bankStatements
      ?.filter((transaction) => cashFlowTransactionId.includes(transaction.id))
      .map((transaction) => {
        if (transaction.accountName === undefined && transaction.accountType !== undefined) {
          return {
            ...transaction,
            accountName: intl.formatMessage(accountMessages[transaction.accountType], {
              index: transaction.accountIndex,
            }),
          };
        }

        return transaction;
      });

  //Alert if the transactions in the cashflow are not empty while the bank statements list is empty
  if (
    cashFlow &&
    cashFlow.transactions !== undefined &&
    cashFlow.transactions.length > 0 &&
    (transactionsList === undefined || transactionsList.length === 0)
  ) {
    monitoring.logError({
      fingerprint: 'cashflow_modal_empty_transactions',
      message: 'In the cashflow modal, the bank statements list is empty while the cashflow has transactions',
      context: {
        data: {
          bankStatements,
          transactionsList,
        },
      },
    });
  }

  const categoriesList = totalCashFlow > 0 ? categoriesListEntry : categoriesListOut;
  const natureTransaction =
    totalCashFlow > 0 ? intl.formatMessage(messages.incoming) : intl.formatMessage(messages.outgoing);

  const isNewCategory = selectedCategory !== cashFlow.category;

  categoriesList.sort((a, b) =>
    translateTransactionCategory(a.category).localeCompare(translateTransactionCategory(b.category)),
  );

  const isCashFlowSummed = 'isSummed' in cashFlow && cashFlow.isSummed;

  return (
    <S.Container data-testid="cashflow-modal">
      <S.ModalTitle>{intl.formatMessage(messages.modalTitle)}</S.ModalTitle>
      <S.CashFlowsInformation>
        <S.FolderIdCell cell="label">
          <S.CellTitle>{intl.formatMessage(messages.commonBankStatement)}</S.CellTitle>
          <S.CellContent
            type={
              cashFlow.labelRoot === 'unknown' || cashFlow.labelRoot === '' || isCashFlowSummed ? 'unknown' : 'label'
            }
          >
            {cashFlow.labelRoot === 'unknown' || cashFlow.labelRoot === '' || isCashFlowSummed
              ? 'N/A'
              : cashFlow.labelRoot}
          </S.CellContent>
        </S.FolderIdCell>
        <S.FolderIdCell cell="transaction">
          <S.CellTitle>{intl.formatMessage(messages.transactions)}</S.CellTitle>
          <S.CellContent>
            {cashFlow && cashFlow.transactions !== undefined && cashFlow.transactions.length}
            <S.CellTitle> {intl.formatMessage(messages.for)} </S.CellTitle>
            {cashFlow && totalCashFlow && <FormattedMoney number={totalCashFlow} />}
          </S.CellContent>
        </S.FolderIdCell>
        <S.FolderIdCell cell="type">
          <S.CellTitle>{intl.formatMessage(messages.type)} </S.CellTitle>
          <S.CellContent>{translateTransactionType(type)}</S.CellContent>
        </S.FolderIdCell>
      </S.CashFlowsInformation>

      {oldCategory && (
        <S.Category>
          <S.ModalSubtitle>{intl.formatMessage(messages.oldCategory)}</S.ModalSubtitle>
          <S.OldCategory>{translateTransactionCategory(oldCategory)}</S.OldCategory>
        </S.Category>
      )}

      <S.Category>
        <S.ModalSubtitle>
          {intl.formatMessage(oldCategory ? messages.modalCurrentCategory : messages.modalCategory)}
        </S.ModalSubtitle>
        {selectedCategory && (
          <ModalDropdown
            currentCategory={newCategory}
            selectedCategory={selectedCategory}
            setSelectedCategory={setSelectedCategory}
            categoriesList={categoriesList}
            dropDownHeader={natureTransaction}
            isDisabled={isCashFlowSummed}
            oldCategory={oldCategory}
          />
        )}
      </S.Category>
      <Divider />
      <S.ModalSubtitle>{intl.formatMessage(messages.modalTransaction)}</S.ModalSubtitle>
      <ModalTransactionSection transactionsList={transactionsList} analysisId={analysisId} customerId={customerId} />
      <S.LargeDivider isNewCategory={isNewCategory} />

      {!oldCategory && isNewCategory && (
        <S.ValidationMessage>{intl.formatMessage(messages.validationMessage)}</S.ValidationMessage>
      )}

      <S.ButtonSection data-testid="close-cashflow-modal">
        {isNewCategory && (
          <S.Button
            variant="tertiary"
            onClick={() => {
              onClose();
            }}
          >
            {intl.formatMessage(messages.modalCancelButton)}
          </S.Button>
        )}
        <S.Button
          data-testid="recategorization-submit"
          onClick={async () => {
            // despite the await appearing useless, this seems fixes a bug that
            // prevents the modal from closing properly, causing a scroll lock.
            await onClose();
            if (isNewCategory) onChangeCategory(selectedCategory);
          }}
        >
          {intl.formatMessage(messages.modalSubmitButton)}
        </S.Button>
      </S.ButtonSection>
    </S.Container>
  );
};

const S = {
  Container: styled.div`
    width: 745px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
    border-radius: 4px;
  `,
  CashFlowsInformation: styled.div`
    display: flex;
    width: 100%;
    min-height: 80px;
    background: ${({ theme }) => theme.secondary.chalk};
    border-radius: 4px;
    margin-bottom: 24px;
  `,
  ModalTitle: styled(Typography.H2)`
    margin-bottom: 24px;
  `,
  Category: styled.div`
    width: 100%;
    height: 45px;
    margin-bottom: 24px;
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: center;
  `,
  ValidationMessage: styled.div`
    color: ${({ theme }) => theme.algoanBlue};
    width: 100%;
    height: 16px;
    font-size: 13px;
    text-align: center;
    margin-top: 18px;
    margin-bottom: 18px;
  `,
  LargeDivider: styled(Divider)<{ isNewCategory: boolean }>`
    width: 800px;
    align-self: center;
    border-top: 2px solid ${({ theme }) => addAlpha(theme.algoanBlue, 0.05)};
    margin-top: 24px;
    margin-bottom: 24px;
  `,
  ModalSubtitle: styled(Typography.H3)`
    font-weight: normal;
    width: 150px;
  `,
  ButtonSection: styled.div`
    width: 100%;
    height: 40px;
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
  `,
  Button: styled(Button)`
    width: 300px;
    height: 40px;
    margin-left: 8px;
    margin-right: 8px;
  `,
  FolderIdCell: styled.div<{ cell: 'label' | 'transaction' | 'type' }>`
    padding: 16px;
    box-sizing: border-box;
    & > :first-child {
      margin-bottom: 12px;
    }
    border-right: 2px solid ${({ theme }) => theme.white};

    &:last-of-type {
      border-right: none;
    }

    display: flex;
    flex-direction: column;
    align-items: center;

    ${({ cell }) => {
      switch (cell) {
        case 'label':
          return `width: 336px`;
        case 'transaction':
          return `width: 196px`;
        case 'type':
          return `width: 220px`;
        default:
          return ``;
      }
    }};
  `,
  CellContent: styled.div<{ type?: string | undefined }>`
    font-weight: bold;
    font-size: 14px;
    line-height: 18px;
    color: ${({ theme }) => theme.algoanBlue};
    text-align: center;
    ${({ type, theme }) => {
      switch (type) {
        case 'label':
          return `
            font-style: normal;
            font-weight: 600;
            font-size: 13px;
            line-height: 16px;
            text-transform: uppercase;
          `;
        case 'unknown':
          return `
            font-style: normal;
            font-weight: normal;
            color: ${theme.secondary.softBlue1};
          `;
        default:
          return ``;
      }
    }};
  `,
  CellTitle: styled.span`
    font-size: 14px;
    line-height: 16px;
    color: ${({ theme }) => theme.algoanBlue};
  `,
  OldCategory: styled.div`
    width: 350px;
    height: 45px;
    margin-left: 50px;
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    border-radius: 8px;
    background: ${({ theme }) => theme.secondary.chalk};
    color: ${({ theme }) => theme.algoanBlue};
    font-size: 14px;
  `,
  LoadingMessageContainer: styled.div`
    width: 685px;
    height: 498px;
    align-items: center;
    display: flex;
  `,
  LoadingMessage: styled.div`
    margin: auto;
    text-align: center;
    color: ${(props) => props.theme.algoanBlue};
  `,
};
